import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import "./../styles/offerFlipCountdownBanner.scss";

function toDate(v) {
  if (!v) return null;
  if (v instanceof Date) return v;
  if (typeof v === "number") return new Date(v);

  if (typeof v === "string") {
    if (/^\d{4}-\d{2}-\d{2}\s\d{2}:\d{2}:\d{2}$/.test(v)) {
      const d = new Date(v.replace(" ", "T") + "+08:00");
      return isNaN(d.getTime()) ? null : d;
    }
    const d = new Date(v);
    return isNaN(d.getTime()) ? null : d;
  }
  return null;
}

function pad2(n) {
  return String(Math.max(0, n)).padStart(2, "0");
}

function CopyCodeButton({ label, code, amount }) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1300);
    } catch (e) {
      window.prompt("請手動複製優惠碼：", code);
    }
  };

  return (
    <button type="button" className={`bt-copyCodeBtn ${copied ? "is-copied" : ""}`} onClick={copy}>
      <div className="bt-copyMain">{copied ? "已複製 ✓" : `複製 ${label} 優惠碼`}</div>
      <div className="bt-copySub">{code}｜折 NT${amount}</div>
    </button>
  );
}

function FlipUnit({ label, value }) {
  const prevRef = useRef(value);
  const [front, setFront] = useState(value);
  const [back, setBack] = useState(value);
  const [isFlipping, setIsFlipping] = useState(false);

  useLayoutEffect(() => {
    const prev = prevRef.current;
    if (value === prev) return;

    setBack(value);
    setIsFlipping(true);

    const mid = setTimeout(() => setFront(value), 220);
    const end = setTimeout(() => {
      setIsFlipping(false);
      prevRef.current = value;
    }, 520);

    return () => {
      clearTimeout(mid);
      clearTimeout(end);
    };
  }, [value]);

  return (
    <div className="bt-flipUnit">
      <div className={`bt-flipCard ${isFlipping ? "is-flipping" : ""}`}>
        <div className="bt-flipTop">
          <span className="bt-flipNum">{front}</span>
        </div>
        <div className="bt-flipBottom">
          <span className="bt-flipNum">{front}</span>
        </div>
        <div className="bt-flipTopFlip">
          <span className="bt-flipNum">{front}</span>
        </div>
        <div className="bt-flipBottomFlip">
          <span className="bt-flipNum">{back}</span>
        </div>
      </div>
      <div className="bt-flipLabel">{label}</div>
    </div>
  );
}

export default function OfferFlipCountdownBanner({
  endAt,
  discounts = [],
  title = "🔥 初談專屬優惠",
  subtitle = "諮詢評估後 30 天內完成購買享優惠",
  ctaText = "立即選購",
  ctaHref = "/quickbuy",
  show,
  allowClose = true,
}) {
  const [hidden, setHidden] = useState(false);

  const resolvedEndAt = useMemo(() => toDate(endAt), [endAt]);
  const [remainingSec, setRemainingSec] = useState(null);

  useEffect(() => {
    if (!resolvedEndAt) return;

    const endMs = resolvedEndAt.getTime();
    let timer = null;

    const update = () => {
      const now = Date.now();
      const totalMs = endMs - now;

      if (totalMs <= 0) {
        setRemainingSec(0);
        return;
      }

      setRemainingSec(Math.floor(totalMs / 1000));

      const remainder = totalMs % 1000;
      let delay = remainder === 0 ? 1000 : remainder + 1;
      if (delay < 16) delay = 16;

      timer = setTimeout(update, delay);
    };

    update();
    return () => timer && clearTimeout(timer);
  }, [resolvedEndAt]);

  const remaining = useMemo(() => {
    if (!resolvedEndAt || remainingSec === null) return null;

    const days = Math.floor(remainingSec / 86400);
    const hrs = Math.floor((remainingSec % 86400) / 3600);
    const min = Math.floor((remainingSec % 3600) / 60);
    const sec = remainingSec % 60;

    return { totalMs: remainingSec * 1000, days, hrs, min, sec };
  }, [resolvedEndAt, remainingSec]);

  const active = useMemo(() => {
    if (typeof show === "boolean") return show;
    if (hidden) return false;
    if (!remaining) return false;
    return remaining.totalMs > 0;
  }, [show, hidden, remaining]);

  const close = () => setHidden(true);

  const sortedDiscounts = useMemo(() => {
    const order = [39, 40, 41];
    return [...discounts].sort((a, b) => order.indexOf(a.discount_id) - order.indexOf(b.discount_id));
  }, [discounts]);

  const labelMap = (id, fallbackName) => {
    if (id === 39) return "CPA";
    if (id === 40) return "CMA";
    if (id === 41) return "EA";
    return fallbackName || "方案";
  };

  const dd = remaining ? pad2(remaining.days) : "00";
  const hh = remaining ? pad2(remaining.hrs) : "00";
  const mm = remaining ? pad2(remaining.min) : "00";
  const ss = remaining ? pad2(remaining.sec) : "00";

  if (!active) return null;

  return (
    <div className="bt-offerWrap">
      <div className="bt-offerBanner">
        <div className="container bt-offerInner">
          <div className="bt-offerTopRow">
            <div className="bt-offerLeft">
              <div className="bt-offerTitle">{title}</div>
              <div className="bt-offerSub">{subtitle}</div>
            </div>

            <div className="bt-offerCenter">
              <div className="bt-flipClock" aria-label="Offer countdown">
                <FlipUnit label="天" value={dd} />
                <div className="bt-flipSep">:</div>
                <FlipUnit label="時" value={hh} />
                <div className="bt-flipSep">:</div>
                <FlipUnit label="分" value={mm} />
                <div className="bt-flipSep">:</div>
                <FlipUnit label="秒" value={ss} />
              </div>
            </div>

            <div className="bt-offerRight">
              <a className="btn btn-sm btn-warning" href={ctaHref}>
                {ctaText}
              </a>
              {allowClose && (
                <button type="button" className="bt-closeX" aria-label="close" onClick={close}>
                  ×
                </button>
              )}
            </div>
          </div>

          {sortedDiscounts.length > 0 && (
            <div className="bt-offerBottomRow">
              <div className="bt-offerCodes">
                {sortedDiscounts.map(d => (
                  <CopyCodeButton
                    key={d.discount_id}
                    label={labelMap(d.discount_id, d.name)}
                    code={d.code}
                    amount={d.amount}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}